import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { formatISO } from 'date-fns';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { updateInvoicePayment } from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';

const EditpaymentForm = ({ onClose, selectedPayment, chequeData, onSuccess }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  console.log('selectedPayment', selectedPayment);

  // Single payment state (since we're editing only one payment)
  const [payment, setPayment] = useState({
    amount_paid: '',
    payment_date: '',
    payment_mode: '',
    transaction_id: '',
    selected_cheque_id: null
  });

  // Watch payment mode to show/hide fields
  const paymentMode = watch('payment_mode');

  // Handle cheque selection - prefill form fields
  const handleChequeSelection = (chequeId) => {
    const selectedCheque = chequeData.find((cheque) => cheque.id === Number(chequeId));
    if (selectedCheque) {
      // Update payment state with cheque details
      setPayment({
        amount_paid: selectedCheque.amount,
        payment_date: selectedCheque.date
          ? new Date(selectedCheque.date).toISOString().split('T')[0]
          : '',
        payment_mode: 'cheque',
        transaction_id: selectedCheque.cheque_number,
        selected_cheque_id: Number(chequeId)
      });

      // Update form values
      setValue('amount_paid', selectedCheque.amount);
      setValue(
        'payment_date',
        selectedCheque.date ? new Date(selectedCheque.date).toISOString().split('T')[0] : ''
      );
      setValue('transaction_id', selectedCheque.cheque_number);
      setValue('selected_cheque_id', Number(chequeId));
    }
  };

  // Reset form when selectedPayment changes (edit mode)
  useEffect(() => {
    if (selectedPayment) {
      // Extract amount from the formatted string (remove ₹ and commas)
      const amount = selectedPayment.amount_paid
        ? selectedPayment.amount_paid.replace(/[₹,]/g, '')
        : '';

      setPayment({
        amount_paid: amount,
        payment_date: selectedPayment.created_at
          ? new Date(selectedPayment.created_at).toISOString().split('T')[0]
          : '',
        payment_mode: selectedPayment.mode || '',
        transaction_id: selectedPayment.transactionID || '',
        selected_cheque_id: null
      });

      setValue('amount_paid', amount);
      setValue(
        'payment_date',
        selectedPayment.created_at
          ? new Date(selectedPayment.created_at).toISOString().split('T')[0]
          : ''
      );
      setValue('payment_mode', selectedPayment.mode || '');
      setValue('transaction_id', selectedPayment.transactionID || '');

      // If it's a cheque payment, find and set the selected cheque
      if (selectedPayment.mode === 'cheque' && selectedPayment.transactionID && chequeData) {
        const matchingCheque = chequeData.find(
          (cheque) => cheque.cheque_number === selectedPayment.transactionID
        );
        if (matchingCheque) {
          setValue('selected_cheque_id', matchingCheque.id);
          setPayment((prev) => ({ ...prev, selected_cheque_id: matchingCheque.id }));
        }
      }
    }
  }, [selectedPayment, setValue, chequeData]);

  const { mutate: handleUpdatePayment, isPending } = useMutation({
    mutationFn: (data) => {
      let payload;

      if (data.payment_mode === 'cheque' && payment.selected_cheque_id) {
        // For cheque payments, get data from selected cheque
        const selectedCheque = chequeData.find(
          (cheque) => cheque.id === payment.selected_cheque_id
        );
        payload = {
          amount_paid: selectedCheque.amount,
          mode: 'cheque',
          transactionID: selectedCheque.cheque_number,
          date: formatISO(new Date(selectedCheque.date))
        };
      } else {
        // For other payment modes
        payload = {
          amount_paid: Number(data.amount_paid),
          mode: data.payment_mode,
          transactionID: data.payment_mode === 'cash' ? null : data.transaction_id,
          date: formatISO(new Date(data.payment_date))
        };
      }

      return updateInvoicePayment(selectedPayment.id, payload);
    },
    onSuccess: () => {
      toast.success('Payment updated successfully');
      reset();
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Error updating payment');
      console.error('Error updating payment:', error);
    }
  });

  const onSubmit = (data) => {
    handleUpdatePayment(data);
  };

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Payment Mode Selection */}
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Mode <span className="text-red-500">*</span>
          </label>
          <select
            {...register('payment_mode', { required: 'Payment mode is required' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option value="">Select Payment Mode</option>
            <option value="cheque">Cheque</option>
            <option value="upi">UPI</option>
            <option value="internet_banking">Internet Banking</option>
            <option value="rtgs">RTGS</option>
            <option value="nft">NFT</option>
            <option value="cash">Cash</option>
          </select>
          {errors.payment_mode && (
            <p className="text-red-500 text-sm mt-1">{errors.payment_mode.message}</p>
          )}
        </div>

        {/* Cheque Selection - Show when payment mode is cheque */}
        {paymentMode === 'cheque' && (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Cheque <span className="text-red-500">*</span>
            </label>
            <select
              value={payment.selected_cheque_id || ''}
              onChange={(e) => {
                const chequeId = e.target.value;
                if (chequeId) {
                  handleChequeSelection(parseInt(chequeId));
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required>
              <option value="">Select a Cheque</option>
              {chequeData &&
                chequeData.map((cheque) => (
                  <option key={cheque.id} value={cheque.id}>
                    {cheque.cheque_number} - ₹{new Intl.NumberFormat('en-IN').format(cheque.amount)}{' '}
                    - {cheque.account_holder_name}
                  </option>
                ))}
            </select>
          </div>
        )}

        <div className="flex gap-4 items-end w-full">
          <FormInput
            width={49}
            label="Amount Paid"
            isRequired
            register_key="amount_paid"
            type="number"
            register={register}
            error={errors.amount_paid?.message}
            readOnly={paymentMode === 'cheque'}
          />
          <FormInput
            width={49}
            label="Payment Date"
            isRequired
            register_key="payment_date"
            type="date"
            register={register}
            error={errors.payment_date?.message}
            readOnly={paymentMode === 'cheque'}
          />
        </div>

        {/* Transaction ID Field - Show for all modes except cash */}
        {paymentMode && paymentMode !== 'cash' && paymentMode !== 'cheque' && (
          <FormInput
            label={'Transaction ID'}
            register_key="transaction_id"
            type="text"
            register={register}
            error={errors.transaction_id?.message}
          />
        )}

        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Updating...' : 'Update Payment'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditpaymentForm;
